import type { FastFeedSeriesRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import { fastFeedSeriesQuery } from "@/queries/fast-feed";
import { useQuery } from "@tanstack/react-query";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { useEffect, useMemo, useState } from "react";

type Gran = "H" | "D" | "W" | "M";

const GRANS: { key: Gran; label: string; title: string }[] = [
  { key: "H", label: "H", title: "Hourly" },
  { key: "D", label: "D", title: "Daily" },
  { key: "W", label: "W", title: "Weekly" },
  { key: "M", label: "M", title: "Monthly" },
];

function fmtTs(ts: string, gran: Gran): string {
  if (gran === "H") {
    const [datePart, timePart] = ts.split(" ");
    const [, mm, dd] = datePart.split("-");
    return `${Number(mm)}/${Number(dd)} ${timePart.slice(0, 2)}h`;
  }
  if (gran === "W") {
    const d = new Date(`${ts}T00:00:00Z`);
    const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
    const wk = Math.ceil(
      ((tmp.getTime() - new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1)).getTime()) / 86_400_000 +
        1) /
        7,
    );
    return `W${String(wk).padStart(2, "0")}`;
  }
  if (gran === "M") {
    const [yyyy, mm] = ts.split("-");
    return `${yyyy}/${mm}`;
  }
  const [, mm, dd] = ts.split("-");
  return `${Number(mm)}/${Number(dd)}`;
}

function ChartInner({
  rows,
  gran,
  cc,
}: { rows: FastFeedSeriesRow[]; gran: Gran; cc: ReturnType<typeof useChartColors> }) {
  const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

  const visiblePct = zoomRange.end - zoomRange.start;
  const visibleCount =
    rows.length > 0 ? Math.max(1, Math.round((visiblePct / 100) * rows.length)) : 999;
  const useBar = visibleCount <= 60 && gran !== "M";

  const option = useMemo<EChartsOption>(() => {
    const labels = rows.map((r) => fmtTs(r.ts, gran));
    const fewPts = rows.length <= 5;

    return {
      grid: { left: 56, right: 16, top: 12, bottom: 56 },
      dataZoom: [
        {
          type: "slider",
          xAxisIndex: 0,
          bottom: 4,
          height: 26,
          borderColor: "transparent",
          backgroundColor: "rgba(255,255,255,0.03)",
          fillerColor: "rgba(59,130,246,0.14)",
          handleStyle: { color: "#fff", borderColor: "#3b82f6", borderWidth: 1.5 },
          handleSize: "80%",
          moveHandleStyle: { color: "#3b82f6" },
          dataBackground: {
            lineStyle: { color: "#3b82f6", width: 1, opacity: 0.35 },
            areaStyle: { color: "rgba(59,130,246,0.08)", opacity: 1 },
          },
          selectedDataBackground: {
            lineStyle: { color: "#3b82f6", width: 1.5 },
            areaStyle: { color: "rgba(59,130,246,0.18)" },
          },
          textStyle: { color: "transparent", fontSize: 0 },
          emphasis: { handleLabel: { show: false }, handleStyle: { borderColor: "#60a5fa" } },
        },
        { type: "inside", xAxisIndex: 0 },
      ],
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        axisTick: { show: false },
        boundaryGap: useBar,
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `$${v.toFixed(0)}` },
        splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
      },
      tooltip: {
        trigger: "axis",
        formatter: (p: unknown) => {
          const params = p as Array<{ name: string; value: number; dataIndex: number }>;
          if (!params[0]) return "";
          const r = rows[params[0].dataIndex];
          const dao = r ? `$${r.treasury_usdg.toFixed(2)}` : "";
          return `<b>${params[0].name}</b><br/>Revenue: $${(+params[0].value).toFixed(2)}<br/>DAO: ${dao}`;
        },
      },
      series: [
        useBar
          ? {
              type: "bar",
              data: rows.map((r) => +r.revenue_usdg.toFixed(2)),
              barMaxWidth: 20,
              itemStyle: {
                color: {
                  type: "linear" as const,
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: "#3b82f6" },
                    { offset: 1, color: "rgba(59,130,246,0.35)" },
                  ],
                },
                borderRadius: [2, 2, 0, 0],
              },
            }
          : {
              type: "line",
              data: rows.map((r) => +r.revenue_usdg.toFixed(2)),
              smooth: true,
              symbol: fewPts ? "circle" : "none",
              symbolSize: 6,
              lineStyle: { color: "#3b82f6", width: 2 },
              itemStyle: { color: "#3b82f6" },
              areaStyle: {
                color: {
                  type: "linear" as const,
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: "rgba(59,130,246,0.28)" },
                    { offset: 1, color: "rgba(59,130,246,0)" },
                  ],
                },
              },
            },
      ],
    };
  }, [rows, gran, cc, useBar]);

  const ref = useECharts(option, [option]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ref.current is set by useECharts before this effect runs; stable DOM ref
  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.getInstanceByDom(ref.current);
    if (!chart) return;
    const handler = (params: unknown) => {
      const p = params as {
        start?: number;
        end?: number;
        batch?: Array<{ start: number; end: number }>;
      };
      const start = p.start ?? p.batch?.[0]?.start ?? 0;
      const end = p.end ?? p.batch?.[0]?.end ?? 100;
      setZoomRange({ start, end });
    };
    chart.on("datazoom", handler);
    return () => {
      chart.off("datazoom", handler);
    };
  }, []);

  return <div ref={ref} style={{ height: 240 }} />;
}

export function FastFeedEarningsChart() {
  const [gran, setGran] = useState<Gran>("D");
  const cc = useChartColors();
  const { data: rows = [], isLoading } = useQuery(fastFeedSeriesQuery(gran));

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "2px 8px",
    fontSize: 11,
    background: active ? "var(--color-accent)" : "transparent",
    color: active ? "#fff" : "var(--color-text-muted)",
    border: "1px solid",
    borderColor: active ? "var(--color-accent)" : "var(--color-border)",
    borderRadius: "var(--radius-badge)",
    cursor: "pointer",
  });

  return (
    <Panel
      title="EARNINGS"
      action={
        <div style={{ display: "flex", gap: 4 }}>
          {GRANS.map((g) => (
            <button
              key={g.key}
              type="button"
              title={g.title}
              style={btnStyle(gran === g.key)}
              onClick={() => setGran(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
      }
    >
      {isLoading ? (
        <div className="skeleton" style={{ height: 240, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No data available
        </div>
      ) : (
        <ChartInner rows={rows} gran={gran} cc={cc} />
      )}
    </Panel>
  );
}
